// Forked from ../../ResultsDropdown/GroupMetaproperties/index.tsx — keep in sync until phase_0 cleanup.
import React from 'react';
import styled from 'styled-components';
import { token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import type { MetapropertiesOption, SearchMetaproperty } from '../../types';
import { navigateTo } from '../../../../helpers/windowLocation';
import { executeSearch, getMetapropertySearchField } from '../../helpers/searchUtils';
import { MetapropertySearchParams } from '../../helpers/types';
import { sortMetapropertyOptions } from '../../helpers/sortMetapropertyOptions';
import { NavigableChip } from '../SharedComponents';
import { useSearch, useSearchMode } from '../../SpotlightState';

type Props = {
	metaproperty: SearchMetaproperty;
};

export function GroupMetaproperties({ metaproperty }: Props) {
	const { clearResults } = useSearch();
	const { isHybridSearchOn } = useSearchMode();
	const { translate } = useTranslate();

	const shouldGoToPIMOnClick = metaproperty.spotlightFilterAction === 'pim';

	const sortedOptions = sortMetapropertyOptions(metaproperty);

	const handleOptionClick = (option: MetapropertiesOption) => {
		if (shouldGoToPIMOnClick) {
			navigateTo(`/show/${metaproperty.name}/${option.id}`);

			return;
		}

		const params: MetapropertySearchParams = {
			field: getMetapropertySearchField(metaproperty.name),
			value: option.name,
			filterType: 'add',
			filterKey: 'savedFilters',
		};

		const isPerformingNLSSearch = false;

		// The metaproperty and option travel next to `params` because SearchParams flattens them
		// into `metaproperty_<name>` + a value, which the asset-bank/search topic cannot express.
		executeSearch(isHybridSearchOn, params, isPerformingNLSSearch, { metaproperty, option });
		clearResults();
	};

	return (
		<StyledRow>
			<StyledLabel>{translate('spotlight.dropdown.metaproperty')}</StyledLabel>
			<StyledChipRow>
				{sortedOptions.map((option) => (
					<NavigableChip key={option.id} onSelect={() => handleOptionClick(option)}>
						{option.label}
					</NavigableChip>
				))}
			</StyledChipRow>
		</StyledRow>
	);
}

const StyledRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${token.spacing4};
	padding: ${token.spacing2} ${token.spacing5};
`;

const StyledLabel = styled.div`
	width: auto;
	font-weight: 500;
	color: ${token.colorTextMuted};
`;

const StyledChipRow = styled.div`
	display: flex;
	flex: 1;
	min-width: 0;
	gap: ${token.spacing4};
	overflow-x: auto;
`;
